import { Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import { ClockStatus } from "generated-graphql/graphql";
import { ReactElement } from "react";

import { deriveRuleSource } from "./clockRuleSource";
import { CLOCK_STATUS_LABELS } from "./containerDisplay";

/**
 * NONE (no applicable limit, or an unresolvable generator status) takes the
 * neutral role — it isn't a problem state, just the absence of one.
 */
const CLOCK_STATUS_CHIP_COLORS: Record<ClockStatus, StatusChipColor> = {
  [ClockStatus.Ok]: "success",
  [ClockStatus.Approaching]: "warning",
  [ClockStatus.OverLimit]: "error",
  [ClockStatus.Overdue]: "error",
  [ClockStatus.None]: "default",
};

export type ChipLimit = { basis: string; citation: string; jurisdiction: string };

/**
 * Explains where a clock reading comes from — which rule, which citation,
 * whose jurisdiction. Wraps whatever renders the reading (the chip, or the
 * days-remaining text in the list's Clock column); with no limits to name it
 * renders its child untouched, so callers don't have to branch.
 */
export function ClockLimitTooltip({
  limits,
  children,
}: {
  limits: ChipLimit[] | undefined;
  children: ReactElement;
}) {
  if (!limits || limits.length === 0) {
    return children;
  }
  return (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          {limits.map((limit, index) => (
            <Typography key={`${limit.basis}-${index}`} variant="caption" component="div">
              {`${limit.basis} · ${limit.citation} · ${deriveRuleSource(limit).label}`}
            </Typography>
          ))}
        </Stack>
      }
    >
      {/* An element of the tooltip's own to anchor to: neither StatusChip nor
          the text cell forwards a ref. */}
      <span style={{ display: "inline-flex" }}>{children}</span>
    </Tooltip>
  );
}

/**
 * The clock's state, as the light-filled chip the design system specs and the
 * waste frames draw — matching the status chip it sits beside.
 */
export function WasteContainerClockChip({
  status,
  limits,
}: {
  status: ClockStatus;
  /**
   * The limits behind this chip's status, for a hover tooltip explaining
   * which rule produced it. Omitted (or empty) on list screens, which keep
   * the plain chip — only screens that already fetch `clock.limits` pass it.
   */
  limits?: ChipLimit[];
}) {
  return (
    <ClockLimitTooltip limits={limits}>
      <StatusChip
        label={CLOCK_STATUS_LABELS[status]}
        color={CLOCK_STATUS_CHIP_COLORS[status]}
        variant="soft"
      />
    </ClockLimitTooltip>
  );
}
